/**
 * AgentOsDemoSocialMedia.jsx — sandbox for Social Media Scheduler.
 */
import { useState } from "react";
import axios from "axios";
import { API } from "../config";
import { Sparkles, ArrowRight, Loader2, Copy, CheckCircle, Share2 } from "lucide-react";
import AgentOsDemoShell from "./AgentOsDemoShell";

const PLATFORMS = [
  { id: "instagram", name: "Instagram" }, { id: "facebook", name: "Facebook" },
  { id: "x", name: "X (Twitter)" }, { id: "linkedin", name: "LinkedIn" },
];
const VOICES = [{ id: "warm", name: "Vrúcny" }, { id: "professional", name: "Profesionálny" }, { id: "witty", name: "Vtipný" }, { id: "luxury", name: "Luxusný" }];
const EXAMPLES = [
  { product_name: "Drevené hodinky Aurora", key_features: "sandalwood, gravírovanie zdarma, vodotesné", brand_name: "WoodTime", platform: "instagram" },
  { product_name: "Premium kávový set", key_features: "francúzsky lis 1L + 2 šálky, ručná výroba", brand_name: "Caffé Nero", platform: "facebook" },
  { product_name: "B2B CRM Platform launch", key_features: "AI lead scoring, GDPR-native, EU servers", brand_name: "SalesPro", platform: "linkedin" },
];

export default function AgentOsDemoSocialMedia() {
  const [form, setForm] = useState({
    product_name: "", product_url: "", key_features: "",
    platform: "instagram", brand_name: "", brand_voice: "warm", lang: "sk",
  });
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState({ loading: false, error: null });
  const [copied, setCopied] = useState(false);

  const loadExample = (i) => { setForm((f) => ({ ...f, ...EXAMPLES[i] })); setResult(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_name.trim()) return;
    setStatus({ loading: true, error: null }); setResult(null);
    try {
      const r = await axios.post(`${API}/agent-os/demo/social-media`, form);
      setResult(r.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setStatus({ loading: false, error: typeof d === "object" ? d : { message: d || "Skús to ešte raz." } });
      return;
    }
    setStatus({ loading: false, error: null });
  };

  const copyAll = async () => {
    if (!result?.output) return;
    const o = result.output;
    const txt = `${o.caption}\n\n${(o.hashtags || []).map((h) => `#${h}`).join(" ")}\n\nCTA: ${o.cta}\nBest time: ${o.best_time}`;
    try { await navigator.clipboard.writeText(txt); setCopied(true); } catch {}
    setTimeout(() => setCopied(false), 2000);
  };

  const Left = (
    <form onSubmit={submit} className="p-6 bg-black border border-gray-800" data-testid="social-media-form">
      <div className="flex gap-2 mb-5 flex-wrap">
        {EXAMPLES.map((_, i) => (
          <button key={i} type="button" onClick={() => loadExample(i)}
            data-testid={`social-media-example-${i}`}
            className="text-[10px] px-3 py-1 border border-emerald-700/40 text-emerald-300 hover:bg-emerald-900/20 font-bold uppercase tracking-widest">
            Príklad {i + 1}
          </button>
        ))}
      </div>
      <Field label="Produkt"><input required value={form.product_name} maxLength={200}
        onChange={(e) => setForm({ ...form, product_name: e.target.value })}
        className="w-full px-4 py-2.5 bg-neutral-950 border border-gray-800 text-white text-sm placeholder-gray-600 focus:border-emerald-500 outline-none"
        placeholder="napr. Drevené hodinky Aurora" data-testid="social-media-input-product" />
      </Field>
      <Field label="URL produktu (voliteľné)"><input value={form.product_url} maxLength={400}
        onChange={(e) => setForm({ ...form, product_url: e.target.value })}
        className="w-full px-4 py-2.5 bg-neutral-950 border border-gray-800 text-white text-sm placeholder-gray-600 focus:border-emerald-500 outline-none"
        placeholder="https://eshop.sk/produkt/aurora" data-testid="social-media-input-url" />
      </Field>
      <Field label="Kľúčové vlastnosti"><textarea value={form.key_features} maxLength={500}
        onChange={(e) => setForm({ ...form, key_features: e.target.value })} rows={3}
        className="w-full px-4 py-2.5 bg-neutral-950 border border-gray-800 text-white text-sm placeholder-gray-600 focus:border-emerald-500 outline-none resize-none"
        placeholder="materiály, USP, sezónnosť..." data-testid="social-media-input-features" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Platform"><select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}
          className="w-full px-4 py-2.5 bg-neutral-950 border border-gray-800 text-white text-sm" data-testid="social-media-input-platform">
          {PLATFORMS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select></Field>
        <Field label="Brand voice"><select value={form.brand_voice} onChange={(e) => setForm({ ...form, brand_voice: e.target.value })}
          className="w-full px-4 py-2.5 bg-neutral-950 border border-gray-800 text-white text-sm" data-testid="social-media-input-voice">
          {VOICES.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select></Field>
      </div>
      <Field label="Značka"><input value={form.brand_name} maxLength={80}
        onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
        className="w-full px-4 py-2.5 bg-neutral-950 border border-gray-800 text-white text-sm placeholder-gray-600 focus:border-emerald-500 outline-none"
        placeholder="WoodTime" data-testid="social-media-input-brand" />
      </Field>
      <button type="submit" disabled={status.loading || !form.product_name}
        data-testid="social-media-submit"
        className="w-full mt-2 py-3 bg-emerald-400 hover:bg-emerald-300 disabled:bg-gray-700 disabled:text-gray-400 text-black font-black uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2">
        {status.loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generujem…</> : <><Sparkles className="w-4 h-4" /> Generuj post</>}
      </button>
    </form>
  );

  const Right = (
    <div className="p-6 bg-black border border-gray-800" data-testid="social-media-output">
      {!result && !status.loading && !status.error && <Placeholder Icon={Share2} text="Vyplň formulár a vygeneruj platform-native sociálny post" />}
      {status.loading && <Loading text="AI vyberá najlepšie slová pre tvoj post…" />}
      {status.error && <ErrorBox err={status.error} />}
      {result?.output && (
        <div className="space-y-4 text-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">Caption</span>
            <button onClick={copyAll} data-testid="social-media-copy" className="inline-flex items-center gap-1 text-[10px] px-2 py-1 border border-emerald-700/40 text-emerald-300 hover:bg-emerald-900/20 font-bold uppercase tracking-widest">
              {copied ? <><CheckCircle className="w-3 h-3" /> Kopírované</> : <><Copy className="w-3 h-3" /> Kopírovať</>}
            </button>
          </div>
          <p className="text-gray-200 leading-relaxed whitespace-pre-wrap" data-testid="social-media-output-caption">{result.output.caption}</p>
          <div className="flex flex-wrap gap-1.5" data-testid="social-media-output-hashtags">
            {(result.output.hashtags || []).map((h) => <span key={h} className="text-xs text-emerald-300 bg-emerald-900/30 px-2 py-1 border border-emerald-700/40">#{h}</span>)}
          </div>
          <div className="pt-3 border-t border-gray-900 grid grid-cols-2 gap-3 text-xs">
            <div><p className="text-gray-500 mb-1">CTA</p><p className="text-gray-200 font-semibold">{result.output.cta}</p></div>
            <div><p className="text-gray-500 mb-1">Best time</p><p className="text-gray-200 font-semibold">{result.output.best_time}</p></div>
          </div>
          {result.output.image_prompt && (
            <div className="pt-3 border-t border-gray-900 text-xs"><p className="text-gray-500 mb-1">Image brief</p><p className="text-gray-300 italic">{result.output.image_prompt}</p></div>
          )}
          <p className="text-[10px] text-gray-600 pt-3 border-t border-gray-900">Zostáva ti dnes {result.remaining_today} demo runov. Latencia: {result.latency_ms}ms.</p>
        </div>
      )}
    </div>
  );

  return <AgentOsDemoShell testid="agent-os-demo-social-media" h1="Social Media Scheduler" lead="Platform-native posty pre IG/FB/X/LinkedIn za 5 sekúnd. AI vyberie caption, hashtagy, best time." left={Left} right={Right} />;
}

const Field = ({ label, children }) => (<label className="block mb-3"><span className="block text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1.5">{label}</span>{children}</label>);
const Placeholder = ({ Icon, text }) => (<div className="text-center py-10"><Icon className="w-10 h-10 text-gray-700 mx-auto mb-3" /><p className="text-sm text-gray-500">{text}</p></div>);
const Loading = ({ text }) => (<div className="text-center py-10"><Loader2 className="w-10 h-10 text-emerald-400 animate-spin mx-auto mb-3" /><p className="text-sm text-gray-400">{text}</p></div>);
const ErrorBox = ({ err }) => (<div className="p-4 border border-red-900/60 bg-red-950/30 text-sm text-red-300" data-testid="social-media-error">{err.message || "Skús to znova."}</div>);
